import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DateRange } from 'react-day-picker';
import { RegionalHeader } from '../../regional/components/RegionalHeader';
import { LocationsList } from '../../regional/components/LocationsList';
import { RegionalMetricsTable } from '../../regional/RegionalMetricsTable';
import { MapPin } from 'lucide-react';
import { PremiumDonutChart } from '@/components/metrics/charts/PremiumDonutChart';

interface RegionalTabContentProps {
  date?: DateRange | undefined;
  videoId: string | null;
  regionalData?: any;
  metricsEnhanced?: any;
}

interface LocationData {
  id: string;
  name: string;
  code: string;
  views: number;
  percentage: number;
  flag?: string;
  type?: 'country' | 'state' | 'city';
}

// Mock data for countries
const countriesMockData: LocationData[] = [
  { id: '1', name: 'Brasil', code: 'BR', views: 45023, percentage: 68.5 },
  { id: '2', name: 'EUA', code: 'US', views: 12456, percentage: 19.0 },
  { id: '3', name: 'Portugal', code: 'PT', views: 5234, percentage: 8.0 },
  { id: '4', name: 'Argentina', code: 'AR', views: 2890, percentage: 4.4 }
];

// Mock data for states (Brazilian states)
const statesMockData: LocationData[] = [
  { id: '1', name: 'São Paulo', code: 'SP', views: 15234, percentage: 33.8, type: 'state' },
  { id: '2', name: 'Rio de Janeiro', code: 'RJ', views: 12453, percentage: 27.7, type: 'state' },
  { id: '3', name: 'Minas Gerais', code: 'MG', views: 9876, percentage: 21.9, type: 'state' },
  { id: '4', name: 'Paraná', code: 'PR', views: 7234, percentage: 16.1, type: 'state' }
];

// Mock data for cities
const citiesMockData: LocationData[] = [
  { id: '1', name: 'São Paulo', code: 'SP', views: 8234, percentage: 18.3, type: 'city' },
  { id: '2', name: 'Rio de Janeiro', code: 'RJ', views: 6543, percentage: 14.5, type: 'city' },
  { id: '3', name: 'Salvador', code: 'BA', views: 4321, percentage: 9.6, type: 'city' },
  { id: '4', name: 'Curitiba', code: 'PR', views: 3876, percentage: 8.6, type: 'city' }
];

// Colors for pie chart
const COLORS = [
  '#5B7FFF', // Azul vibrante
  '#FF6B9D', // Rosa
  '#FFA040', // Laranja
  '#00D4AA', // Verde água
  '#A78BFA', // Roxo
  '#60A5FA'  // Azul claro
];

// Custom tooltip for pie chart
const CustomTooltip = ({ active, payload }: any) => {
  useLanguage();
  if (active && payload && payload.length) {
    return (
      <div className="bg-white px-4 py-3 border border-[#E2E8FF] rounded-[12px] shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
        <p className="text-[14px] font-semibold text-[#0A1A2F] mb-1">{payload[0].name}</p>
        <p className="text-[13px] text-[#6B7280]">
          {payload[0].value.toLocaleString(getInterfaceLanguage())} {" "}{translateInterfaceText("visualizações")}</p>
        <p className="text-[12px] font-medium text-[#2D66F5] mt-1">
          {payload[0].payload.percentage}{translateInterfaceText("% do total")}</p>
      </div>
    );
  }
  return null;
};

// Component for rendering cities list
function CitiesList({ data }: { data: LocationData[] }) {
  useLanguage();
  return (
    <div className="space-y-3">
      {data.slice(0, 3).map((city, index) => (
        <div key={index} className="flex items-center justify-between bg-[#F7F9FF] border border-[#E2E8FF] rounded-[12px] px-[18px] py-[16px] min-h-[62px]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#F2F4F7] flex items-center justify-center">
              <MapPin className="h-5 w-5 text-[#7E8492]" />
            </div>
            <div>
              <p className="text-[14px] font-medium text-[#1A1A1A]">{city.name}</p>
              <p className="text-[13px] font-normal text-[#6B7280]">{city.percentage}{translateInterfaceText("% das visualizações")}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[15px] font-semibold text-[#1A1A1A]">
              {city.views.toLocaleString(getInterfaceLanguage())}
            </p>
            <p className="text-[11px] text-[#9CA3AF]">{translateInterfaceText("visualizações")}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function RegionalTabContent({
  date,
  videoId,
  regionalData,
  metricsEnhanced
}: RegionalTabContentProps) {
  useLanguage();
  const countries = metricsEnhanced?.countries || [];
  const states = metricsEnhanced?.states || [];
  const cities = metricsEnhanced?.cities || [];
  const pitches = metricsEnhanced?.videoInfo?.pitches ?? [];

  // Mapear países da API para o formato esperado pelo componente
  const countriesData: LocationData[] = countries
    .map((country, index) => ({
      id: index.toString(),
      name: country.pais === 'BR' ? 'Brasil' : country.pais === '0' ? 'Desconhecido' : country.pais,
      code: country.pais || 'XX',
      views: country.views,
      percentage: parseFloat(country.percentual),
      type: 'country' as const
    }))
    .sort((a, b) => b.views - a.views); // Ordenar do maior para o menor

  // Mapear estados da API para o formato esperado pelo componente
  const statesData: LocationData[] = states
    .map((state, index) => ({
      id: index.toString(),
      name: state.estado === '0' ? 'Desconhecido' : state.estado,
      code: state.estado || 'XX',
      views: state.views,
      percentage: parseFloat(state.percentual),
      type: 'state' as const
    }))
    .sort((a, b) => b.views - a.views); // Ordenar do maior para o menor

  // Mapear cidades da API para o formato esperado pelo componente
  const citiesData: LocationData[] = cities
    .map((city, index) => ({
      id: index.toString(),
      name: city.cidade === '0' || !city.cidade ? 'Desconhecido' : city.cidade,
      code: city.cidade || 'XX',
      views: city.views,
      percentage: parseFloat(city.percentual),
      type: 'city' as const
    }))
    .sort((a, b) => b.views - a.views); // Ordenar do maior para o menor

  return (
    <div className="space-y-6">
      <RegionalHeader />

      <Tabs defaultValue="countries" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger
            value="countries"
            className="data-[state=active]:bg-[#2D6CF6] data-[state=active]:text-white data-[state=active]:shadow-sm"
          >
            {translateInterfaceText("Países")}</TabsTrigger>
          <TabsTrigger
            value="states"
            className="data-[state=active]:bg-[#2D6CF6] data-[state=active]:text-white data-[state=active]:shadow-sm"
          >
            {translateInterfaceText("Estados")}</TabsTrigger>
          <TabsTrigger
            value="cities"
            className="data-[state=active]:bg-[#2D6CF6] data-[state=active]:text-white data-[state=active]:shadow-sm"
          >
            {translateInterfaceText("Cidades")}</TabsTrigger>
        </TabsList>

        <TabsContent value="countries">
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            {/* Gráfico Donut Premium */}
            <PremiumDonutChart
              title={translateInterfaceText("Distribuição por Países")}
              data={countriesData.map(country => ({
                name: country.name,
                value: country.views,
                percentage: country.percentage
              }))}
              renderLegends={false}
            />

            {/* Detalhes por país */}
            <Card className="border border-[#E2E8FF] rounded-[14px] bg-white p-4 md:p-[22px] md:min-h-[260px]">
              <h3 className="text-[16px] font-semibold text-[#1A1A1A] mb-[18px]">{translateInterfaceText("Detalhes por País")}</h3>
              <div className="space-y-3">
                {countriesData.length > 0 ? (
                  <LocationsList data={countriesData} colors={COLORS} type="country" />
                ) : (
                  <p className="text-center text-[#6B7280] py-8">{translateInterfaceText("Nenhum dado de país disponível")}</p>
                )}
              </div>
            </Card>
          </div>

          <div className="mt-6">
            <RegionalMetricsTable type="countries" data={countries} pitches={pitches} />
          </div>
        </TabsContent>

        <TabsContent value="states">
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            {/* Gráfico Donut Premium */}
            <PremiumDonutChart
              title={translateInterfaceText("Distribuição por Estados")}
              data={statesData.map(state => ({
                name: state.name,
                value: state.views,
                percentage: state.percentage
              }))}
              renderLegends={false}
            />

            {/* Detalhes por estado */}
            <Card className="border border-[#E2E8FF] rounded-[14px] bg-white p-4 md:p-[22px] md:min-h-[260px]">
              <h3 className="text-[16px] font-semibold text-[#1A1A1A] mb-[18px]">{translateInterfaceText("Detalhes por Estado")}</h3>
              <div className="space-y-3">
                {statesData.length > 0 ? (
                  <LocationsList data={statesData} colors={COLORS} type="state" />
                ) : (
                  <p className="text-center text-[#6B7280] py-8">{translateInterfaceText("Nenhum dado de estado disponível")}</p>
                )}
              </div>
            </Card>
          </div>

          <div className="mt-6">
            <RegionalMetricsTable type="states" data={states} pitches={pitches} />
          </div>
        </TabsContent>

        <TabsContent value="cities">
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            {/* Gráfico Donut Premium */}
            <PremiumDonutChart
              title={translateInterfaceText("Distribuição por Cidades")}
              data={citiesData.map(city => ({
                name: city.name,
                value: city.views,
                percentage: city.percentage
              }))}
              renderLegends={false}
            />

            {/* Detalhes por cidade */}
            <Card className="border border-[#E2E8FF] rounded-[14px] bg-white p-4 md:p-[22px] md:min-h-[260px]">
              <h3 className="text-[16px] font-semibold text-[#1A1A1A] mb-[18px]">{translateInterfaceText("Detalhes por Cidade")}</h3>
              <div className="space-y-3">
                {citiesData.length > 0 ? (
                  <LocationsList data={citiesData} colors={COLORS} type="city" />
                ) : (
                  <p className="text-center text-[#6B7280] py-8">{translateInterfaceText("Nenhum dado de cidade disponível")}</p>
                )}
              </div>
            </Card>
          </div>

          <div className="mt-6">
            <RegionalMetricsTable type="cities" data={cities} pitches={pitches} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
